import { ApiError, RunnerBoyClient } from '@runnerboy/api-client';
import type { AuthUserDto, AvailabilityDto, CartDto } from '@runnerboy/types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { apiOrigin, isNative, nativeRefreshStore } from './native';

export const client = new RunnerBoyClient({ baseUrl: apiOrigin(), app: 'CONSUMER', refreshStore: isNative ? nativeRefreshStore : undefined });

export interface PublicConfig {
  environment: string;
  mapStyleUrl: string;
  paymentProvider: string;
  testMode: boolean;
}

/** Fictional demo territory centre (DEMO-A) — offered only when the API reports test mode. */
export const DEMO_LOCATION: SelectedLocation = { lat: 12.9719, lng: 77.6452, label: 'Demo City A1 · simulation', addressId: null };

export interface SelectedLocation {
  lat: number;
  lng: number;
  label: string;
  addressId?: string | null;
}

const LOC_KEY = 'rb.location';
const AGE_KEY = 'rb.ageGate';
const STORE_KEY = 'rb.store';

function read<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}
function write(key: string, v: unknown) {
  try {
    if (v === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* storage unavailable (private mode) — state stays in memory */
  }
}

interface SessionState {
  ready: boolean;
  user: AuthUserDto | null;
  config: PublicConfig | null;
  location: SelectedLocation | null;
  setLocation(l: SelectedLocation | null): void;
  availability: AvailabilityDto | null;
  availabilityLoading: boolean;
  /** Error code when availability could not be resolved (e.g. OUTSIDE_INDIA, NETWORK). */
  availabilityError: string | null;
  ageGate: 'confirmed' | 'declined' | null;
  setAgeGate(v: 'confirmed' | 'declined'): void;
  storeId: string | null;
  setStoreId(id: string | null): void;
  cart: CartDto | null;
  refreshCart(): Promise<void>;
  setCart(c: CartDto): void;
  loginOpen: boolean;
  requireLogin(): boolean;
  setLoginOpen(v: boolean): void;
  logout(): Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUserDto | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [location, setLocationState] = useState<SelectedLocation | null>(() => read(LOC_KEY));
  const [availability, setAvailability] = useState<AvailabilityDto | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [ageGate, setAgeGateState] = useState<'confirmed' | 'declined' | null>(() => read(AGE_KEY));
  const [storeId, setStoreIdState] = useState<string | null>(() => read(STORE_KEY));
  const [cart, setCart] = useState<CartDto | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    client.opts.onSessionChange = (u) => setUser(u);
    void Promise.allSettled([client.restore(), client.get<PublicConfig>('/public/config').then(setConfig)]).finally(() => setReady(true));
  }, []);

  const refreshCart = useCallback(async () => {
    if (!userRef.current) return setCart(null);
    try {
      setCart(await client.get<CartDto>(`/cart${location?.addressId ? `?addressId=${location.addressId}` : ''}`));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setCart(null);
    }
  }, [location?.addressId]);

  useEffect(() => {
    void refreshCart();
  }, [user, refreshCart]);

  useEffect(() => {
    if (!location) return setAvailability(null);
    let cancelled = false;
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    client
      .get<AvailabilityDto>(`/public/availability?lat=${location.lat}&lng=${location.lng}`)
      .then((a) => !cancelled && setAvailability(a))
      .catch((e) => {
        if (cancelled) return;
        setAvailability(null);
        setAvailabilityError(e instanceof ApiError ? e.code : 'UNKNOWN');
      })
      .finally(() => !cancelled && setAvailabilityLoading(false));
    return () => {
      cancelled = true;
    };
  }, [location]);

  const value = useMemo<SessionState>(
    () => ({
      ready,
      user,
      config,
      location,
      setLocation: (l) => {
        write(LOC_KEY, l);
        setLocationState(l);
      },
      availability,
      availabilityLoading,
      availabilityError,
      ageGate,
      setAgeGate: (v) => {
        write(AGE_KEY, v);
        setAgeGateState(v);
      },
      storeId,
      setStoreId: (id) => {
        write(STORE_KEY, id);
        setStoreIdState(id);
      },
      cart,
      refreshCart,
      setCart,
      loginOpen,
      setLoginOpen,
      requireLogin: () => {
        if (userRef.current) return true;
        setLoginOpen(true);
        return false;
      },
      logout: async () => {
        await client.logout();
        setCart(null);
      },
    }),
    [ready, user, config, location, availability, availabilityLoading, availabilityError, ageGate, storeId, cart, refreshCart, loginOpen],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider missing');
  return v;
}

/** Minimal data-fetching hook (no global cache: keeps bundle small and avoids caching personal data). */
export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[], opts: { enabled?: boolean } = {}) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(opts.enabled !== false);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (opts.enabled === false) {
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fn(ctrl.signal)
      .then((d) => !ctrl.signal.aborted && setData(d))
      .catch((e) => !ctrl.signal.aborted && setError(e))
      .finally(() => !ctrl.signal.aborted && setLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, opts.enabled]);
  return { data, error, loading, reload: () => setNonce((n) => n + 1), setData };
}
